import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import apiClient from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export function PaymentSuccess() {
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setDetails(data);
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= 8) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 8) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="payment-success-page">
      <Navbar />
      <section className="pt-40 pb-24 px-6 md:px-12 lg:px-24 min-h-[80vh] flex items-center justify-center">
        <div className="max-w-xl text-center lk-card p-12">
          {status === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto animate-spin mb-6" />
              <h1 className="font-serif text-3xl mb-3">Confirming your payment…</h1>
              <p className="text-zinc-400">One luxurious moment. We're verifying with Stripe.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto mb-6" />
              <h1 className="font-serif text-4xl mb-3">
                {details?.payment_type === "balance" ? "Balance settled." : "You're booked."}
              </h1>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                {details?.payment_type === "balance"
                  ? "Your booking is now fully paid. We can't wait to celebrate with you on event day."
                  : "Deposit received and your date is secured. Our team will be in touch within 24 hours to finalize details."}
              </p>
              {details?.booking_id && (
                <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 mb-8 text-left">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Save your booking ID</div>
                  <div className="font-mono text-sm text-white break-all" data-testid="success-booking-id">
                    {details.booking_id}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-2">
                    Use this on the <Link to="/find-booking" className="text-[#D4AF37] underline">Find Booking</Link> page to view or cancel.
                  </div>
                </div>
              )}
              <Link to="/" data-testid="back-home" className="lk-btn-primary text-xs inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Return Home
              </Link>
            </>
          )}
          {(status === "failed" || status === "expired") && (
            <>
              <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
              <h1 className="font-serif text-3xl mb-3">Payment not confirmed</h1>
              <p className="text-zinc-400 mb-8">
                We couldn't verify your payment. If you were charged, please contact us with your receipt.
              </p>
              <button onClick={() => navigate("/book")} className="lk-btn-primary text-xs">
                Try Again
              </button>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="payment-cancel-page">
      <Navbar />
      <section className="pt-40 pb-24 px-6 md:px-12 lg:px-24 min-h-[80vh] flex items-center justify-center">
        <div className="max-w-xl text-center lk-card p-12">
          <XCircle className="w-14 h-14 text-zinc-400 mx-auto mb-6" />
          <h1 className="font-serif text-3xl mb-3">Checkout cancelled</h1>
          <p className="text-zinc-400 mb-8">No charges were made. Your booking is on hold — pick up where you left off whenever you're ready.</p>
          <Link to="/book" className="lk-btn-primary text-xs">
            Return to Booking
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
